import { DateTimeInfo, DateTimeInfoKind } from "../../DateTimeInfo";
import { Nullable, Nullish, PartialNullable } from "../../Extensions";
import { StringHelper } from "../../Helpers/StringHelper";
import { IJNativeEvent, JNative, JNativeStatic } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean } from "../../RadObject";
import { IInputControl, InputControl, InputControlPropertyValues } from "./InputControl";
import { NAMESPACE } from "./_Namespace";

import "./DateTime.scss";

export type DateTimePropertyValues =
    InputControlPropertyValues
    & {
        isLocal: boolean;
    };

export type DateTimeProperties = {
    [name in keyof DateTimePropertyValues]: Property<DateTimePropertyValues[name]>
};

export interface IDateTime extends IInputControl, DateTimeProperties
{
}

export class DateTime extends InputControl implements IDateTime
{
    // #region Static

    // #region Class Identity

    public static override classNamespace = NAMESPACE + "DateTime";
    public static override className = "DateTime";

    // #endregion

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<DateTimePropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                isLocal: false,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected $$valueElement?: JNative<HTMLInputElement>;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<DateTimePropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-date-time ui input form");
    }

    // #endregion

    // #region Properties

    public isLocal!: PropertyBoolean;

    // #endregion

    // #region Protected Methods

    protected _elementValueChanged()
    {
        if (!this.__processElementValueChanged)
            return;

        const
            valueLocal = this._elementValueGet(),
            value =
                valueLocal
                    ?
                    (this.isLocal.get()
                        ? DateTimeInfo.fromDateString(valueLocal, DateTimeInfoKind.local).toString("yyyy-MM-ddTHH:mm:ss.fffffff")
                        : DateTimeInfo.fromDateString(valueLocal, DateTimeInfoKind.local).toIsoString())
                    : null;

        this.value.set(value);
    }
    protected _elementValueGet(): Nullish<string>
    {
        return this.$$valueElement?.val();
    }
    protected _elementValueSet(value: Nullable<string>)
    {
        this.__processElementValueChanged = false;
        this.$$valueElement?.val(value);
        this.__processElementValueChanged = true;
    }

    // #endregion

    // #region Private Methods

    private _getLocalDateTimeInfo(value: string)
    {
        return (
            value
                ?
                (this.isLocal.get()
                    ? DateTimeInfo.fromDateString(value, DateTimeInfoKind.local)
                    : DateTimeInfo.fromDateString(value, DateTimeInfoKind.utc).toLocal())
                : null
        );
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: DateTimePropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.text.overrideGet(next =>
        {
            const
                value = this.value.get();

            if (!value)
                return null;

            if (typeof value !== "string")
                return null;

            const
                dateTimeInfo = this._getLocalDateTimeInfo(value);

            return dateTimeInfo ? dateTimeInfo.toString("dd/MM/yyyy HH:mm:ss") : null;
        });
        this.value.overrideSet((value, next) =>
        {
            if (!value || !StringHelper.isDate(value, "yyyy-MM-ddTHH:mm:ss.fffffffZ"))
                value = null;

            const
                dateTimeInfo = this._getLocalDateTimeInfo(value);

            this._elementValueSet(dateTimeInfo ? dateTimeInfo.toString("yyyy-MM-ddTHH:mm") : null);
            next(value);
        });

        this.isLocal = new PropertyBoolean(this, propertyValues.isLocal);
    }

    protected override _bindEvents()
    {
        super._bindEvents();

        this.$$valueElement?.on("change", { self: this }, this.onValueElementChanged);
    }
    protected override _createElement()
    {
        const
            $$element = super._createElement(),
            inputId = `${this.controlId}_input`;

        this.$$valueElement = JNativeStatic.create$$({
            nodeName: "input",
            attributes: {
                id: inputId,
                type: "datetime-local",
            }
        }) as JNative<HTMLInputElement>;

        this.$$valueElement.appendTo($$element);

        this.labelTargetId = inputId;

        return $$element;
    }
    protected override _enabledChanged(change: PropertyChange<boolean>)
    {
        super._enabledChanged(change);

        if (change.currentValue)
            this.$$valueElement?.removeAttr("disabled");
        else
            this.$$valueElement?.attr("disabled", "disabled");
    }

    // #endregion

    // #region Event Handlers

    protected onValueElementChanged(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self._elementValueChanged();
    }

    // #endregion

    // #endregion
}
